import { useRef } from 'react';

export interface SegmentedControlOption {
  value: string;
  label: string;
  /** When > 0, renders a count pill inside the button. Hidden when 0 or undefined. */
  badge?: number;
}

interface SegmentedControlProps {
  options: SegmentedControlOption[];
  value: string;
  onChange: (value: string) => void;
}

export function SegmentedControl({ options, value, onChange }: SegmentedControlProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    let nextIndex: number;
    if (e.key === 'ArrowRight') {
      nextIndex = (index + 1) % options.length;
    } else if (e.key === 'ArrowLeft') {
      nextIndex = (index - 1 + options.length) % options.length;
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChange(options[index].value);
      return;
    } else {
      return;
    }

    e.preventDefault();
    const buttons = containerRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    if (buttons) {
      const targetButton = buttons[nextIndex];
      if (targetButton) {
        targetButton.focus();
        onChange(options[nextIndex].value);
      }
    }
  }

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      className="w-full flex items-stretch rounded-md bg-[#1c1a17] p-0.5 gap-0.5"
      data-testid="segmented-control"
      data-segmented-layout="true"
    >
      {options.map((option, i) => {
        const isActive = option.value === value;
        const showBadge = typeof option.badge === 'number' && option.badge > 0;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            data-active={isActive ? 'true' : 'false'}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            className={[
              'flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded text-[10px] font-bold uppercase tracking-widest transition-all duration-150',
              isActive
                ? 'bg-[#302d29] text-[#e8e5e0] shadow-sm'
                : 'text-[#9a9590] hover:text-[#c4c0bb]',
            ].join(' ')}
          >
            {option.label}
            {showBadge && (
              <span
                data-testid={`prep-filter-badge-${option.value}`}
                style={{
                  fontSize: '9px',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  backgroundColor: isActive ? 'var(--color-accent)' : 'var(--color-surface)',
                  color: isActive ? 'var(--color-text-display)' : 'var(--color-text-muted)',
                  borderRadius: '4px',
                  padding: '1px 4px',
                  lineHeight: 1.4,
                }}
              >
                {option.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
